import { Component, ErrorInfo, ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary] caught render error', error, info.componentStack)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  /**
   * A failed dynamic import can't be retried in place: React.lazy caches the
   * rejected promise, so re-rendering re-throws it instantly. Only a reload
   * (which discards the poisoned promise) can recover — so don't offer a
   * button that is guaranteed to do nothing.
   */
  private isChunkError(): boolean {
    return /dynamically imported module|Importing a module script failed|ChunkLoadError/i
      .test(this.state.error?.message ?? '')
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <div className="min-h-screen bg-[#FAF1DD] flex items-center justify-center p-6">
        <div className="max-w-md w-full rounded-2xl border border-red-200 bg-white p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-red-50 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <h1 className="text-lg font-semibold text-gray-900">Something went wrong</h1>
          </div>
          <p className="text-sm text-gray-600 mb-2">
            {this.isChunkError()
              ? "This tab is running an older version of the app and couldn't load the rest of it. Reloading should pick up the current version."
              : "The page hit an error and couldn't finish rendering."}
          </p>
          {this.state.error?.message && (
            <pre className="text-xs bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-auto whitespace-pre-wrap break-words text-gray-700 mb-4 max-h-48">
              {this.state.error.message}
            </pre>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-full bg-[#987628] px-4 py-2 text-sm font-medium text-white hover:bg-[#7a5f20]"
            >
              Reload
            </button>
            {!this.isChunkError() && (
              <button
                type="button"
                onClick={this.handleReset}
                className="rounded-full border border-black/10 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Try again
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }
}
